import { delay, put, select, takeLeading, takeEvery } from 'redux-saga/effects';
import axios from 'axios';
import * as Immutable from 'immutable';
import {
  TutorialActionTypes,
  SyncChapterComplete,
  TutorialCompletionMap,
} from 'Types/TutorialTypes';
import { hydrateCompletion } from 'Actions/tutorial';
import { TutorialReducerState } from 'Reducers/tutorialReducer';
import { awsRum } from 'Utils/AwsRum';
import { actionTypes } from 'Types';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

interface TutorialProgressResponse {
  data: TutorialCompletionMap;
}

function hasEntries(completionMap: TutorialCompletionMap): boolean {
  return Object.keys(completionMap).length > 0;
}

type LocalCompleted = Immutable.Map<number, Immutable.Set<number>> | Record<string, number[]>;

function isImmutableMap(
  value: LocalCompleted,
): value is Immutable.Map<number, Immutable.Set<number>> {
  return Immutable.Map.isMap(value);
}

function localCompletedIsEmpty(completed: LocalCompleted): boolean {
  if (isImmutableMap(completed)) {
    return completed.size === 0;
  }
  return Object.keys(completed).length === 0;
}

function localCompletionToChapterList(
  completed: LocalCompleted,
): Array<{ level_number: number; chapter: number }> {
  const entries: Array<{ level_number: number; chapter: number }> = [];
  if (isImmutableMap(completed)) {
    completed.forEach((chapters, level) => {
      chapters.forEach((chapter) => {
        entries.push({ level_number: level, chapter });
      });
    });
  } else {
    Object.entries(completed).forEach(([levelStr, chapters]) => {
      const level = parseInt(levelStr, 10);
      chapters.forEach((chapter) => {
        entries.push({ level_number: level, chapter });
      });
    });
  }
  return entries;
}

// Chapters completed on this device that the server does not hold: saved
// offline, before progress was kept on the server, or by a save that failed.
export function chaptersMissingOnServer(
  local: LocalCompleted,
  server: TutorialCompletionMap,
): Array<{ level_number: number; chapter: number }> {
  return localCompletionToChapterList(local).filter(
    ({ level_number, chapter }) => !(server[level_number] ?? []).includes(chapter),
  );
}

function* fetchTutorialProgress(): Generator<unknown, void, unknown> {
  try {
    const response: TutorialProgressResponse = yield axios.get<TutorialCompletionMap>(
      baseUrl + 'tutorial-progress',
    );

    const localState: TutorialReducerState = yield select(({ tutorialReducer }) => tutorialReducer);
    const missing = localCompletedIsEmpty(localState.completed)
      ? []
      : chaptersMissingOnServer(localState.completed, response.data);

    if (missing.length > 0) {
      // The catch-up sync: the server marks these rows as synced later and
      // answers with the whole completion map.
      const synced = (yield axios.post<TutorialCompletionMap>(baseUrl + 'tutorial-progress/bulk', {
        chapters: missing,
      })) as TutorialProgressResponse;
      yield put(hydrateCompletion(synced.data));
    } else if (hasEntries(response.data)) {
      yield put(hydrateCompletion(response.data));
    }
  } catch (err) {
    awsRum?.recordError(err);
    console.error('Failed to fetch tutorial progress:', err);
  }
}

// A save is retried after a growing pause while a retry can succeed (no response,
// a 5xx, a timeout or a rate limit). A chapter whose save still fails stays in
// the device's completed set and goes up with the next startup's catch-up sync.
export const CHAPTER_SAVE_RETRY_DELAYS_MS = [2000, 4000, 8000];

export const isRetryableSaveFailure = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return status === undefined || status >= 500 || status === 408 || status === 429;
};

function* syncChapterComplete(action: SyncChapterComplete): Generator<unknown, void, unknown> {
  const body = {
    level_number: action.payload.lesson,
    chapter: action.payload.chapter,
  };
  for (const [attempt, retryDelayMs] of [0, ...CHAPTER_SAVE_RETRY_DELAYS_MS].entries()) {
    if (retryDelayMs > 0) yield delay(retryDelayMs);
    try {
      yield axios.post(baseUrl + 'tutorial-progress', body);
      return;
    } catch (err) {
      const lastAttempt = attempt === CHAPTER_SAVE_RETRY_DELAYS_MS.length;
      if (lastAttempt || !isRetryableSaveFailure(err)) {
        awsRum?.recordError(err);
        console.error('Failed to sync tutorial chapter completion:', err);
        return;
      }
    }
  }
}

export default [
  // takeLeading, matching AppData's watcher of the same action: an authenticated
  // boot dispatches AUTHENTICATED_STARTUP twice (App.tsx on the isAuthenticated
  // flip, AuthWrapperContext on the restored OIDC session), so takeLatest fetched
  // tutorial progress twice — the first request is already on the wire by the time
  // takeLatest cancels its saga. Confirmed in a browser network trace, MUS-402.
  takeLeading(actionTypes.AUTHENTICATED_STARTUP, fetchTutorialProgress),
  takeEvery(TutorialActionTypes.SYNC_CHAPTER_COMPLETE, syncChapterComplete),
];
